import React from 'react';
import { useParseContractContextRequired } from 'features/tasks-forms';
import { ValidationError } from 'nekst-api';
import {
  CheckboxInput,
  Header4,
  List,
  Right,
  SimpleBlock,
  Text,
  useListContextRequired,
  useShowContextRequired,
} from 'ui-builder';
import { VerifyDataEntry } from '../FromContract/ValidateData/types';
import { ParsedValueField } from '../FromContract/ValidateData/ParsedValueField';
import { Popup, PopupText, RowRepeater } from 'features/nekst-widgets';

import styles from '../FromContract/ValidateData/ValidateData.module.scss';

export function DetailBoxNameField() {
  const showContext = useShowContextRequired<VerifyDataEntry>();
  return (
    <Text className={styles.boxName}>{showContext.data!.name}</Text>
  );
}

export function ApplyParseDataValidationPopup(props: { error: ValidationError }) {
  const getFieldLabel = (fieldId: string) => {
    switch (fieldId) {
      case 'startDate':
        return 'Start Date';
      case 'endDate':
        return 'End Date';
      case 'name':
        return 'Transaction Name';
      default:
        return fieldId;
    }
  };

  return (
    <Popup
      title="Validation Error"
      content={
        <PopupText align="left">
          Please correct the following errors:
          <ul>
            {Object.entries(props.error.errors)
              .map(([fieldId, error]) => (
                <li key={fieldId}>{getFieldLabel(fieldId)}: {error.join('; ')}</li>
              ))}
          </ul>
        </PopupText>
      }
    />
  );
}

export function SelectedFieldsCounter() {
  const listContext = useListContextRequired();
  const parseContractContext = useParseContractContextRequired();

  return (
    <span
      className={styles.fieldsCounter}
      data-testid="selected-fields-counter"
      title="Use the checkboxes below to mark the fields you have already verified. This will help ensure all details are reviewed and accurate."
    >
      {parseContractContext.getNumberOfSelectedFields()} / {listContext.data.length}
    </span>
  );
}

export function DetailFieldsRow(props: {
  renderValueOverride?: (entry: VerifyDataEntry) => React.ReactNode | undefined;
}) {
  const showContext = useShowContextRequired<VerifyDataEntry>();
  const parseContractContext = useParseContractContextRequired();

  const data = showContext.data!;
  const valueOverride = props.renderValueOverride?.(data);
  const isDisabled = valueOverride !== undefined;

  return (
    <SimpleBlock
      testID="detail-boxes-list"
      className={[
        styles.boxWidget,
        parseContractContext.isFieldSelected(data.detailsField?.id || 0) ? styles.selected : '',
        isDisabled ? styles.disabled : '',
      ].join(' ')}
    >
      <SimpleBlock className={styles.boxWidgetInside}>
        <DetailBoxNameField />
        {valueOverride !== undefined ? valueOverride : <ParsedValueField />}
      </SimpleBlock>
      <CheckboxInput
        source="isVerified"
        label="Correct"
        hideLabel
        value={isDisabled || parseContractContext.isFieldSelected(data.detailsField?.id || 0)}
        onChangeCallback={() => parseContractContext.toggleFieldSelection(data.detailsField?.id || 0)}
      />
    </SimpleBlock>
  );
}

export function DetailFieldsList(props: {
  verifyDataEntries: VerifyDataEntry[];
  renderValueOverride?: (entry: VerifyDataEntry) => React.ReactNode | undefined;
}) {
  return (
    <List getDataFunc={async () => props.verifyDataEntries}>
      <Right>
        <SelectedFieldsCounter />
      </Right>
      <Header4>Detail Boxes</Header4>
      <RowRepeater blockClassName={styles.boxesList} groupClassName={styles.boxWidgetOuter} idField="detailsField.id">
        <DetailFieldsRow renderValueOverride={props.renderValueOverride} />
      </RowRepeater>
    </List>
  );
}
